import { useState } from "react";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import {
  getCurrentOfferIneligibility,
  getPickedVariantId,
  resolveCurrentOffer,
  type CurrentOfferAudience,
} from "@fitness-app/data-models/domain/currentOffer/resolveCurrentOffer";
import { CheckoutVariantTarget, type CheckoutVariant } from "@fitness-app/data-models/entities/CheckoutVariant";

import { usePermission } from "~/hooks/usePermission";
import { useCurrentOfferConfig } from "./useCurrentOfferConfig";

/** Renewal variants go to the link for current clients, the rest to the one for new clients. */
export const currentOfferAudienceOf = (variant: Pick<CheckoutVariant, "target">): CurrentOfferAudience =>
  variant.target === CheckoutVariantTarget.Renewal ? "renewal" : "new";

/**
 * Wybór promocji dla /aktualna-oferta i /aktualna-oferta-przedluzenie na liście koszyków produktu
 * (TRE-2388). Widoczny tylko na produkcie ustawionym w konfiguracji; zmienia go ten, kto
 * zarządza koszykami.
 */
export const useCurrentOfferPicker = (productId: string, variants: CheckoutVariant[] | undefined) => {
  const { configQuery, saveMutation } = useCurrentOfferConfig();
  const canManageCheckout = usePermission(PermissionKey.SALES_CHECKOUT_MANAGE, PermissionScope.ALL);
  const canManageAppConfig = usePermission(PermissionKey.APP_CONFIG_MANAGE, PermissionScope.ALL);
  // Fixed when the tab opens: a status, not a clock. Reopening the tab recomputes it.
  const [now] = useState(() => new Date());

  const config = configQuery.data?.data ?? null;

  return {
    isEnabled: !!configQuery.data && config?.productId?.trim() === productId,
    canPick: canManageCheckout || canManageAppConfig,
    isSaving: saveMutation.isLoading,
    getPickedVariantId: (audience: CurrentOfferAudience) => getPickedVariantId(config, audience),
    resolve: (audience: CurrentOfferAudience) => resolveCurrentOffer(config, variants ?? [], audience, now),
    getIneligibility: (variant: CheckoutVariant) =>
      getCurrentOfferIneligibility(variant, currentOfferAudienceOf(variant), now),
    pick: (audience: CurrentOfferAudience, variantId: string) =>
      saveMutation.mutate(audience === "renewal" ? { renewalVariantId: variantId } : { variantId }),
  };
};
